import { makeAutoObservable, runInAction, when } from "mobx";
import { addDays, format, parseISO, startOfWeek, subDays } from "date-fns";
import RootStore from "stores/RootStore";
import compareJobNumbers from "shared/common/utils/compareJobNumbers";
import { FORM_BUILDERS, MMForm } from "./MMProductionStore";
import { LoadStatus, MMLoad } from "./MMLoadStore";
import { mmErrorMessage } from "./mmErrorMessage";

// Steven's automated daily production report (plan item 15): "what did we
// pour yesterday?", aggregated server-side by
// GET /mm-production-items/daily-production?startDate&endDate (a feathers
// sub-path service with its own jwt hook; endDate defaults to startDate,
// dates are strict yyyy-MM-dd).
//
// The report's real purpose (Creede, plan item 5) is to give Stephen and Dutch
// "the values of the production we're making" — so the per-job cubic feet the
// endpoint returns are joined here against MMProject.billingRatePerCuFt, and
// the week's form-builder progress and shipped loads are aggregated off the
// resident MMProductionStore / MMLoadStore data. All of it derives from the
// SAME Mon-Sat window loadData fetches (see reportWeekDates), so the weekly
// sections can never disagree with the per-day numbers.
//
// NOTE: this surface is consumed by Creede today (it replaces the snapshot
// report she compiles by hand). Relocating the same card onto Steven's own
// dashboard is pending the client confirming which login/dashboard Steven
// actually uses (plan item 15 decision).

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Forms with no builder recorded still have to appear somewhere, or in-progress
// work vanishes from the report.
const UNASSIGNED_BUILDER = "Unassigned";

// The plant's three form builders always get a column — an idle builder is
// itself information. "Other" (a picker option, not a person) and the
// unassigned bucket only appear when they actually hold something.
const NAMED_BUILDERS: string[] = FORM_BUILDERS.filter((b) => b !== "Other");
const ALWAYS_SHOWN_BUILDERS = new Set(NAMED_BUILDERS);

// ============================================================================
// TYPES (mirror the backend's DailyProductionByJob / DailyProductionSummary
// in mm-production-items.class.ts)
// ============================================================================

export interface MMDailyProductionJob {
  projectId: string;
  // Both shapes are live. From the day the API's NVARCHAR half deploys this is
  // a string carrying the "3980P"/"3980V" precast/VDT suffix; the endpoint
  // running today still hands back the INT column as a number. Anything reading
  // it must go through valueJob, which settles it to a string.
  jobNumber: string | number | null;
  jobName: string | null;
  productType: string | null;
  marksPoured: number;
  piecesProduced: number;
  cubicFeetProduced: number;
}

export interface MMDailyProductionSummary {
  startDate: string;
  endDate: string;
  marksPoured: number;
  piecesProduced: number;
  cubicFeetProduced: number;
  jobs: MMDailyProductionJob[];
}

// A day's per-job row with the dollar value of what it produced.
export interface MMValuedProductionJob extends MMDailyProductionJob {
  // Narrowed: whichever shape the endpoint sent, the rest of the report sees a
  // string.
  jobNumber: string | null;
  // null = the job has no usable billing rate. MMProjectStore computes the
  // rate as 0 whenever totalCubicFeet is 0 (no contract data entered yet), so
  // a zero rate is indistinguishable from "not priced" and MUST surface as
  // such: a confident $0.00 silently under-reports the production value this
  // report exists to communicate.
  billingRatePerCuFt: number | null;
  // Cu.ft of the job's pieces that failed QC that day (item 20), already left
  // out of producedValue. Not out of cubicFeetProduced.
  failedCubicFeet: number;
  producedValue: number | null;
}

// The two product lines the week is split into (2026-10-16 item 19). A job
// with no product type is precast, as everywhere else.
export type MMReportProductType = "vdt" | "precast";
export const REPORT_PRODUCT_TYPES: MMReportProductType[] = ["vdt", "precast"];
export const REPORT_PRODUCT_LABELS: Record<MMReportProductType, string> = {
  vdt: "VDT",
  precast: "Precast",
};
export const reportProductTypeOf = (productType: string | null | undefined): MMReportProductType =>
  productType === "vdt" ? "vdt" : "precast";

// One day of the report week.
export interface MMReportDay {
  date: string;
  jobs: MMValuedProductionJob[];
  piecesProduced: number;
  cubicFeetProduced: number;
  producedValue: number;
  unvaluedJobCount: number;
  // The day's value per product line (item 19).
  valueByType: Record<MMReportProductType, number>;
}

// One row of the trailing-week strip (item 19): VDT, Precast or Total, each
// with the day's value and the week's running total to that day. Creede: "Do
// VDT Monday… Saturday, precast Monday… Saturday, and then total… this is
// obviously a running total of the week, adding up each previous day… and
// then down here is the weekly total."
export interface MMWeekTypeRow {
  key: MMReportProductType | "total";
  label: string;
  valueByDate: number[];
  runningByDate: number[];
  weekValue: number;
}

// One job's row across the report week: produced value per day, aligned index
// for index to weekDates, plus the week total.
export interface MMWeekJobRow {
  projectId: string;
  jobNumber: string | null;
  jobName: string | null;
  productType: MMReportProductType;
  billingRatePerCuFt: number | null;
  cubicFeetByDate: number[];
  valueByDate: (number | null)[];
  weekCubicFeet: number;
  weekValue: number | null;
}

export interface MMBuilderForm {
  formId: string;
  formNumber: string;
  jobLabel: string;
  completedDate?: string;
}

export interface MMBuilderStatus {
  builder: string;
  inProgress: MMBuilderForm[];
  completedThisWeek: MMBuilderForm[];
}

// A load dated inside the report week, valued at what is billed. `status` is
// carried because a ship date is a plan, not a fact: a load still planned or
// loaded sits in the yard, so the reader has to be able to tell it apart from
// one that actually went out.
export interface MMWeekLoad {
  loadId: string;
  loadNumber: string;
  shipDate: string;
  jobLabel: string;
  carrier: string;
  status: LoadStatus;
  hasShipped: boolean;
  totalCubicFeet: number;
  billedAmount: number;
  isOverridden: boolean;
  isUnpriced: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

// The report defaults to "what we did the previous day": yesterday, walking
// back over Sundays only. Saturday is a real pour day (Creede, 2026-07-21), so
// skipping the weekend here would have hidden every Saturday's production from
// the report Stephen reads on Monday.
export const defaultReportDate = (): string => {
  let day = subDays(new Date(), 1);
  while (day.getDay() === 0) day = subDays(day, 1);
  return format(day, "yyyy-MM-dd");
};

// The 6 days the report covers: MONDAY THROUGH SATURDAY of the business week
// the anchor falls in. Creede: "Even if it's Wednesday right now, all I care
// about is this business week… This is less of a report date and a report
// week" — the artifact is the finished week, Stephen's snapshot.
//
// This used to be a rolling trailing 7 days ending on the anchor, so the
// "week" slid every time he picked a day and always included a Sunday, which
// is never a pour day. Two consequences of the change that callers must
// respect: the window is 6 entries, not 7, and THE ANCHOR IS NO LONGER LAST —
// it sits wherever its weekday falls, so anything that wants the anchor's own
// report must look it up by date, not by position.
//
// A Sunday anchor is not in its own window (startOfWeek walks back to the
// Monday before it); loadData handles that explicitly.
export const reportWeekDates = (anchorDateOnly: string): string[] => {
  const anchor = parseISO(anchorDateOnly);
  // format() throws on Invalid Date, and this runs inside computeds now — an
  // unparseable anchor has no window rather than an exception.
  if (Number.isNaN(anchor.getTime())) return [];
  const monday = startOfWeek(anchor, { weekStartsOn: 1 });
  return Array.from({ length: 6 }, (_, i) =>
    format(addDays(monday, i), "yyyy-MM-dd")
  );
};

// The dashboard's Upcoming Loads window: Monday to Saturday of the business
// week `today` is in, then the week after -- twelve days. A Sunday belongs to
// no business week (reportWeekDates walks it back to the Monday before), so on
// a Sunday the window starts tomorrow.
export const upcomingWeekDates = (today: string): string[] => {
  const thisWeek = reportWeekDates(today);
  if (thisWeek.length === 0) return [];
  const monday =
    thisWeek[5] < today ? format(addDays(parseISO(today), 1), "yyyy-MM-dd") : thisWeek[0];
  return [
    ...reportWeekDates(monday),
    ...reportWeekDates(format(addDays(parseISO(monday), 7), "yyyy-MM-dd")),
  ];
};

// ============================================================================
// STORE
// ============================================================================

class MMDailyReportStore {
  // yyyy-MM-dd of the day the report is for.
  selectedDate: string = defaultReportDate();
  // Full per-job breakdown for the selected day.
  dayReport: MMDailyProductionSummary | null = null;
  // Trailing week: the selected day plus the 6 days before it, oldest first.
  // The endpoint aggregates a range into ONE summary, so per-day totals need
  // one single-day request per day (fired in parallel).
  weekReports: MMDailyProductionSummary[] = [];
  isLoading: boolean = false;
  error: string | null = null;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
    });
    this.rootStore = rootStore;

    // Deliberately NOT auto-loading on sign-in like the other MM stores:
    // this store fires 7 sub-path GETs per load, and until the backend
    // endpoint deploys that would toast an error at every login. The route's
    // beforeEnter calls ensureLoaded() instead (lazy, on first visit).
  }

  private hasLoadedOnce = false;

  // Load once, but not before there is a session to load with.
  //
  // Two bugs found during the 2026-08-31 click-through, both of which showed up
  // as the home dashboard's production card sitting empty until you visited
  // another screen and came back:
  //
  //  1. This fired the moment a route hook called it. Landing on Home straight
  //     after login runs that hook before the socket is authenticated, so the
  //     seven GETs went out against a connection that could not serve them.
  //     Every sibling MM store already waits for sign-in via `when` — this one
  //     did not, because it was written for a route you could only reach by
  //     navigating, i.e. always well after login. The dashboard made Home the
  //     first thing loaded, and that assumption stopped being true.
  //
  //  2. `loadData` latched `hasLoadedOnce` BEFORE awaiting, so that first
  //     failed attempt permanently disabled the retry. Nothing would load it
  //     again for the rest of the session.
  //
  // `when` fires immediately if the predicate is already true, so navigating to
  // the Daily Report mid-session behaves exactly as it did.
  ensureLoaded = () => {
    if (this.hasLoadedOnce || this.isLoading) return;
    when(
      () => !!(this.rootStore.authStore as any)?.isSignedIn,
      () => {
        // Re-check on the way in: two routes calling this before sign-in would
        // otherwise both fire once the predicate flips.
        if (this.hasLoadedOnce || this.isLoading) return;
        void this.loadData();
      }
    );
  };

  // ========================================
  // SERVICE HELPERS
  // ========================================

  private get dailyProductionService() {
    return this.rootStore.app.service("mm-production-items/daily-production");
  }

  private get projectStore() {
    return this.rootStore.mmProjectStore;
  }

  private get productionStore() {
    return this.rootStore.mmProductionStore;
  }

  private get loadStore() {
    return this.rootStore.mmLoadStore;
  }

  // ========================================
  // COMPUTED - THE WEEK WINDOW
  // ========================================

  get weekDates(): string[] {
    return reportWeekDates(this.selectedDate);
  }

  // ========================================
  // COMPUTED - PRODUCTION VALUE
  // ========================================

  // "#3980 Tower Plaza" for a project id. MMProjectStore is loaded globally
  // (no project filter), so this is a lookup against resident data.
  private jobLabel = (projectId: string): string => {
    const project = this.projectStore?.getProjectById(projectId);
    return project ? `#${project.jobNumber} ${project.jobName}` : "Unknown job";
  };

  // The persisted, maintained $/cu.ft rate, or null when there isn't one.
  // Rate 0 counts as absent — see MMValuedProductionJob.billingRatePerCuFt.
  private billingRate = (projectId: string): number | null => {
    const project = this.projectStore?.getProjectById(projectId);
    return project?.billingRatePerCuFt || null;
  };

  // Failed pieces are cast -- the cu.ft produced stays raw -- but are not
  // value produced (2026-10-16 item 20): "keep it in a sense of how many cubic
  // feet are produced… but remove it for our value produced." Floored at 0, so
  // a recast dated on a day with no pour of that job cannot go negative.
  private valueJob =
    (date: string) =>
    (job: MMDailyProductionJob): MMValuedProductionJob => {
      const billingRatePerCuFt = this.billingRate(job.projectId);
      const failedCubicFeet =
        this.productionStore?.recastCubicFeetOn(job.projectId, date) ?? 0;
      // Mock-up pieces at the mock-up rate (item 22): "any phase that is
      // mock-up should be using the mock-up billing amount for its billing
      // rate… daily report." Without a mock-up rate they stay at the job's.
      const mockupCubicFeet = Math.min(
        job.cubicFeetProduced,
        this.productionStore?.mockupCubicFeetProducedOn?.(job.projectId, date) ?? 0
      );
      const mockupRate = this.productionStore?.mockupRateFor?.(job.projectId) ?? null;
      const valuedCubicFeet = Math.max(0, job.cubicFeetProduced - failedCubicFeet - mockupCubicFeet);
      return {
        ...job,
        jobNumber: job.jobNumber == null ? null : String(job.jobNumber),
        billingRatePerCuFt,
        failedCubicFeet,
        producedValue:
          billingRatePerCuFt == null
            ? null
            : Math.round(
                (valuedCubicFeet * billingRatePerCuFt +
                  mockupCubicFeet * (mockupRate ?? billingRatePerCuFt)) *
                  100
              ) / 100,
      };
    };

  // VDT first, then precast, each keeping the endpoint's job order (item 19:
  // "group together precast and VDT"). A stable sort, so within a line the
  // jobs read as they always did.
  private groupedByType = <T extends { productType: string | null }>(rows: T[]): T[] =>
    [...rows].sort(
      (a, b) =>
        REPORT_PRODUCT_TYPES.indexOf(reportProductTypeOf(a.productType)) -
        REPORT_PRODUCT_TYPES.indexOf(reportProductTypeOf(b.productType))
    );

  get dayJobs(): MMValuedProductionJob[] {
    const date = this.dayReport?.startDate ?? this.selectedDate;
    return this.groupedByType((this.dayReport?.jobs ?? []).map(this.valueJob(date)));
  }

  get dayProducedValue(): number {
    return this.dayJobs.reduce((sum, job) => sum + (job.producedValue ?? 0), 0);
  }

  get dayUnvaluedJobCount(): number {
    return this.dayJobs.filter((job) => job.producedValue == null).length;
  }

  // Per-day totals for the whole window, in weekDates order. Days the fetch
  // returned nothing for are still present, as zero days.
  get weekDays(): MMReportDay[] {
    const byDate = new Map(this.weekReports.map((r) => [r.startDate, r]));
    return this.weekDates.map((date) => {
      const summary = byDate.get(date);
      const jobs = this.groupedByType((summary?.jobs ?? []).map(this.valueJob(date)));
      const valueByType = { vdt: 0, precast: 0 };
      jobs.forEach((job) => {
        valueByType[reportProductTypeOf(job.productType)] += job.producedValue ?? 0;
      });
      return {
        valueByType,
        date,
        jobs,
        piecesProduced: summary?.piecesProduced ?? 0,
        cubicFeetProduced: summary?.cubicFeetProduced ?? 0,
        producedValue: jobs.reduce(
          (sum, job) => sum + (job.producedValue ?? 0),
          0
        ),
        unvaluedJobCount: jobs.filter((job) => job.producedValue == null).length,
      };
    });
  }

  // Adding the days up does not double count. The endpoint reports one row per
  // PLACEMENT day, and a placement belongs to exactly one pour date; the legacy
  // fallback branch (marks with no placements) still buckets on the mark's
  // single completion date. Either way a given piece is counted once in the
  // window.
  get weekProducedValue(): number {
    return this.weekDays.reduce((sum, day) => sum + day.producedValue, 0);
  }

  // The week as one sheet: a row per job that produced anything in the window,
  // a column per day. This is Creede's spreadsheet shape — "if we're producing
  // three different jobs, it would need the value of that job produced every
  // day".
  get weekJobRows(): MMWeekJobRow[] {
    const dayCount = this.weekDates.length;
    const rows = new Map<string, MMWeekJobRow>();

    this.weekDays.forEach((day, dayIndex) => {
      day.jobs.forEach((job) => {
        let row = rows.get(job.projectId);
        if (!row) {
          row = {
            projectId: job.projectId,
            jobNumber: job.jobNumber,
            jobName: job.jobName,
            productType: reportProductTypeOf(job.productType),
            billingRatePerCuFt: job.billingRatePerCuFt,
            cubicFeetByDate: Array.from({ length: dayCount }, () => 0),
            valueByDate: Array.from({ length: dayCount }, () => null),
            weekCubicFeet: 0,
            weekValue: job.billingRatePerCuFt == null ? null : 0,
          };
          rows.set(job.projectId, row);
        }
        row.cubicFeetByDate[dayIndex] = job.cubicFeetProduced;
        row.valueByDate[dayIndex] = job.producedValue;
        row.weekCubicFeet += job.cubicFeetProduced;
        if (row.weekValue != null && job.producedValue != null) {
          row.weekValue += job.producedValue;
        }
      });
    });

    // Same order the endpoint returns its own rows in (compareJobNumbers
    // mirrors its ORDER BY), so the week sheet and the day table agree.
    // Grouped VDT then precast (item 19).
    return Array.from(rows.values()).sort(
      (a, b) =>
        REPORT_PRODUCT_TYPES.indexOf(a.productType) -
          REPORT_PRODUCT_TYPES.indexOf(b.productType) ||
        compareJobNumbers(a.jobNumber, b.jobNumber)
    );
  }

  // VDT, Precast and Total across the week, each with a running total.
  get weekTypeRows(): MMWeekTypeRow[] {
    const days = this.weekDays;
    const rowFor = (
      key: MMWeekTypeRow["key"],
      label: string,
      valueOf: (day: MMReportDay) => number
    ): MMWeekTypeRow => {
      const valueByDate = days.map(valueOf);
      let running = 0;
      const runningByDate = valueByDate.map((value) => (running += value));
      return { key, label, valueByDate, runningByDate, weekValue: running };
    };
    return [
      ...REPORT_PRODUCT_TYPES.map((type) =>
        rowFor(type, REPORT_PRODUCT_LABELS[type], (day) => day.valueByType[type])
      ),
      rowFor("total", "Total", (day) => day.producedValue),
    ];
  }

  get weekUnvaluedJobCount(): number {
    return this.weekJobRows.filter((row) => row.weekValue == null).length;
  }

  // ========================================
  // COMPUTED - FORM BUILDERS
  // ========================================

  private toBuilderForm = (form: MMForm): MMBuilderForm => ({
    formId: form.id,
    formNumber: form.formNumber,
    jobLabel: this.jobLabel(form.projectId),
    completedDate: form.completedDate,
  });

  // Which form each builder is on, and which they closed out this week.
  // "On" can only be inferred from status === "in_progress": nothing in this
  // system schedules form building to a day (see printDailySchedule's template
  // deviations), so a builder holding several in-progress forms shows all of
  // them rather than one picked arbitrarily.
  get builderStatuses(): MMBuilderStatus[] {
    const forms = this.productionStore?.forms ?? [];
    const week = new Set(this.weekDates);

    const order = [...NAMED_BUILDERS];
    forms.forEach((form) => {
      const builder = form.builder || UNASSIGNED_BUILDER;
      if (!order.includes(builder)) order.push(builder);
    });

    return order
      .map((builder) => {
        const own = forms.filter(
          (form) => (form.builder || UNASSIGNED_BUILDER) === builder
        );
        return {
          builder,
          inProgress: own
            .filter((form) => form.status === "in_progress")
            .map(this.toBuilderForm),
          completedThisWeek: own
            .filter(
              (form) =>
                form.status === "completed" &&
                !!form.completedDate &&
                week.has(form.completedDate)
            )
            .map(this.toBuilderForm),
        };
      })
      .filter(
        (status) =>
          ALWAYS_SHOWN_BUILDERS.has(status.builder) ||
          status.inProgress.length > 0 ||
          status.completedThisWeek.length > 0
      );
  }

  // ========================================
  // COMPUTED - LOADS SHIPPED THIS WEEK
  // ========================================

  // MMLoadStore holds every load (its fetch has no project filter) and
  // loadsByShipDate is keyed by plain yyyy-MM-dd, so this is a lookup, not a
  // fetch. Chronological across the window.
  get weekLoads(): MMWeekLoad[] {
    return this.loadsShippingOn(this.weekDates);
  }

  /**
   * The dashboard's Upcoming Loads window: THIS business week and the next,
   * anchored on TODAY.
   *
   * It used to read `weekLoads`, which is the Daily Report's week -- anchored on
   * YESTERDAY, because the report is about finished days. On a Monday that is
   * Saturday, so the card showed last week's loads on exactly the morning the
   * week gets planned (Sherman, 09-28 click-through). It also followed whatever
   * date was picked on the Daily Report screen. Next week is included so a
   * Saturday still shows Monday's trucks.
   */
  get upcomingLoads(): MMWeekLoad[] {
    return this.loadsShippingOn(upcomingWeekDates(format(new Date(), "yyyy-MM-dd")));
  }

  get upcomingNotShippedLoadCount(): number {
    return this.upcomingLoads.filter((load) => !load.hasShipped).length;
  }

  private loadsShippingOn = (dates: string[]): MMWeekLoad[] => {
    const loadStore = this.loadStore;
    if (!loadStore) return [];
    const byShipDate = loadStore.loadsByShipDate;

    return dates.flatMap((date) =>
      (byShipDate.get(date) ?? []).map((load: MMLoad) => ({
        loadId: load.id,
        loadNumber: load.loadNumber,
        shipDate: date,
        jobLabel: this.jobLabel(load.projectId),
        carrier: load.carrier,
        status: load.status,
        hasShipped: load.status === "in_transit" || load.status === "delivered",
        totalCubicFeet: load.totalCubicFeet,
        // Never load.invoiceAmount directly — a manual override IS what they
        // bill, and an override of 0 is legitimate.
        billedAmount: loadStore.effectiveInvoiceAmount(load),
        isOverridden: load.invoiceOverride != null,
        // Volume on the truck but nothing computed and nothing overridden:
        // the same missing-rate hole as billingRatePerCuFt, so flag it rather
        // than report $0.00 as the week's shipped value.
        isUnpriced:
          load.invoiceOverride == null &&
          load.invoiceAmount === 0 &&
          load.totalCubicFeet > 0,
      }))
    );
  };

  get weekLoadsValue(): number {
    return this.weekLoads.reduce((sum, load) => sum + load.billedAmount, 0);
  }

  get weekUnpricedLoadCount(): number {
    return this.weekLoads.filter((load) => load.isUnpriced).length;
  }

  get weekNotShippedLoadCount(): number {
    return this.weekLoads.filter((load) => !load.hasShipped).length;
  }

  // ========================================
  // ACTIONS
  // ========================================

  setSelectedDate = (date: string) => {
    // The backend rejects anything that isn't strict yyyy-MM-dd.
    if (!DATE_ONLY.test(date) || date === this.selectedDate) return;
    this.selectedDate = date;
    this.loadData();
  };

  refresh = () => {
    this.loadData();
  };

  loadData = async () => {
    const date = this.selectedDate;

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const dates = reportWeekDates(date);
      if (dates.length === 0) {
        // DATE_ONLY admits impossible dates (2026-13-45): there is no week to
        // report on, and a blank page would read as "no production".
        runInAction(() => {
          this.weekReports = [];
          this.dayReport = null;
          this.error = `${date} is not a valid date`;
          this.isLoading = false;
        });
        return;
      }

      // A Sunday anchor falls outside its own Mon-Sat window, so fetch it
      // alongside rather than showing the day panel as empty. Every other
      // anchor is already in `dates` and costs no extra request.
      const fetchDates = dates.includes(date) ? dates : [...dates, date];

      const results: MMDailyProductionSummary[] = await Promise.all(
        fetchDates.map((d) =>
          this.dailyProductionService.find({
            query: { startDate: d, endDate: d },
          })
        )
      );

      runInAction(() => {
        // A date change while this request was in flight wins.
        if (this.selectedDate !== date) return;
        this.weekReports = results;
        // By DATE, not by position. This was `results[results.length - 1]`,
        // which was correct only while the anchor was guaranteed to be the
        // last element of a trailing window. Under a fixed Mon-Sat week the
        // anchor sits in the middle, so leaving it would have rendered
        // Saturday's numbers under every selected day's heading — a silent,
        // confident wrong answer rather than a visible failure.
        const anchorIndex = fetchDates.indexOf(date);
        this.dayReport =
          anchorIndex === -1 ? null : results[anchorIndex] ?? null;
        this.isLoading = false;
        // Latched HERE, on success, not before the request. Setting it up front
        // meant one failed attempt disabled ensureLoaded for the session.
        this.hasLoadedOnce = true;
      });
    } catch (error) {
      // The endpoint may not be deployed yet (backend deploy/migration lag):
      // surface a toast + a visible error state, never a blank page.
      runInAction(() => {
        if (this.selectedDate !== date) return;
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to load daily production report";
        // Drop the previous day's figures. Leaving them would render another
        // day's dollars under this date's heading once the loading dim lifts,
        // and the error state is gated on there being no report to show.
        this.dayReport = null;
        this.weekReports = [];
        this.isLoading = false;
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to load daily production report"),
        options: { variant: "error" },
      });
    }
  };
}

export default MMDailyReportStore;
